import { z } from '@customerio/hydra-validation';
import type { Identity as UIAPIIdentity, operations } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Identity entity — sending identities (from addresses) for a workspace.
 *
 * Detail-only and mutation-only today: identities are read sideloaded from
 * the environment aggregate (`db/environment`), so this entity exposes no
 * standalone read surface — just the create mutation onboarding needs. The
 * read schema is a passthrough; declare fields here if a normalizing
 * consumer lands.
 */
export type Identity = UIAPIIdentity;

/**
 * The server-side filters the identities endpoint supports, taken from the
 * generated query type: `type` selects a channel (`email`, `twilio`, …),
 * `in_use` narrows to identities a message currently sends from, and `hidden`
 * selects or excludes archived ones. `in_use` has no equivalent field on the
 * returned model, so that narrowing can only happen server-side. An unfiltered
 * read passes `{}`.
 */
export type IdentityFilters = NonNullable<
  operations['get-v1-environments-environment_id-identities']['parameters']['query']
>;

const IdentityFromApi = z.looseObject({ id: z.string() }).pipe(z.custom<Identity>());

/** @public */
export const identity = defineEntity<Identity, Identity, IdentityFilters>({
  name: 'identity',
  scope: 'workspace',
  pk: (ident) => ident.id,
  schema: { read: IdentityFromApi },
});

export const identityKeys = identity.keys;

export function parseIdentity(raw: UIAPIIdentity): Identity {
  return identity.parse(raw);
}
